import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Play } from 'lucide-react';
function ChevronMark({
  className = ''
}: {
  className?: string;
}) {
  return <svg width="48" height="48" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true" data-id="element-85">
      <path d="M50 15L85 45H70L50 28L30 45H15L50 15Z" fill="url(#tyc0)" data-id="element-86" />
      <path d="M50 35L85 65H70L50 48L30 65H15L50 35Z" fill="url(#tyc1)" data-id="element-87" />
      <path d="M50 55L85 85H70L50 68L30 85H15L50 55Z" fill="url(#tyc2)" data-id="element-88" />
      <defs data-id="element-89">
        <linearGradient id="tyc0" x1="50" y1="15" x2="50" y2="45" gradientUnits="userSpaceOnUse" data-id="element-90">
          <stop stopColor="#0E1A35" data-id="element-91" />
          <stop offset="1" stopColor="#2A3A5C" data-id="element-92" />
        </linearGradient>
        <linearGradient id="tyc1" x1="50" y1="35" x2="50" y2="65" gradientUnits="userSpaceOnUse" data-id="element-93">
          <stop stopColor="#0E1A35" data-id="element-94" />
          <stop offset="1" stopColor="#2A3A5C" data-id="element-95" />
        </linearGradient>
        <linearGradient id="tyc2" x1="50" y1="55" x2="50" y2="85" gradientUnits="userSpaceOnUse" data-id="element-96">
          <stop stopColor="#0E1A35" data-id="element-97" />
          <stop offset="1" stopColor="#2A3A5C" data-id="element-98" />
        </linearGradient>
      </defs>
    </svg>;
}
function DashedDivider() {
  return <div className="max-w-3xl mx-auto px-6" data-id="element-99">
      <div className="border-t border-dashed border-bg-primary/15" data-id="element-100" />
    </div>;
}
function FadeUp({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.4,
    ease: 'easeOut',
    delay
  }} data-id="element-101">
      {children}
    </motion.div>;
}
const qualifiers = ["You're a $250M+ producer at a wirehouse or captive team — Merrill, Morgan Stanley, UBS, Wells, JPM Private Bank — and the last comp grid update was the third paper cut in a row.", "Your firm just got bought, restructured, or rolled into something with a different name — and the place you signed up for isn't the place you'll be working at next year.", "Fifteen-plus years in, you've built a book worth millions, you keep roughly forty cents on the dollar, and you own none of it — and that math has stopped sitting right with you.", "You'd already be gone if it weren't for the TRO risk, the deferred comp you'd forfeit, and the operational headache of going independent — and you want someone who's done this before to handle that part, quietly."];
export function ThankYou() {
  return <div className="min-h-screen bg-white text-bg-primary font-sans selection:bg-accent selection:text-bg-primary" data-id="element-102">
      {/* Centered chevron mark */}
      <div className="w-full flex justify-center pt-12 md:pt-16 pb-12" data-id="element-103">
        <ChevronMark data-id="element-104" />
      </div>

      {/* 1. Confirmation Headline */}
      <section className="w-full px-6 md:px-12 pb-16 md:pb-24" data-id="element-105">
        <div className="max-w-3xl mx-auto text-center" data-id="element-106">
          <FadeUp data-id="element-107">
            <span className="inline-block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-bg-primary/60 mb-6" data-id="element-108">
              Session Tentatively Reserved
            </span>
          </FadeUp>
          <FadeUp delay={0.05} data-id="element-109">
            <h1 className="font-display font-extrabold text-[36px] md:text-[52px] leading-[1.1] tracking-tightest text-bg-primary mb-8" data-id="element-110">
              Your Strategy Session Is Tentatively Held.
            </h1>
          </FadeUp>
          <FadeUp delay={0.1} data-id="element-111">
            <p className="text-lg md:text-xl leading-relaxed text-bg-primary/70 max-w-2xl mx-auto" data-id="element-112">
              We hold each session for one founder at a time. To lock in your
              spot, we'll text you in the next hour to confirm a few details —
              please reply when you see it so we can finalize the time.
            </p>
          </FadeUp>
        </div>
      </section>

      <DashedDivider data-id="element-113" />

      {/* 2. Founder Video */}
      <section className="w-full px-6 md:px-12 py-20 md:py-28" data-id="element-114">
        <div className="max-w-3xl mx-auto" data-id="element-115">
          <FadeUp data-id="element-116">
            <span className="block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-bg-primary/60 mb-6 text-center" data-id="element-117">
              A 90-Second Note Before The Call
            </span>
          </FadeUp>
          <FadeUp delay={0.05} data-id="element-118">
            <h2 className="font-display font-bold text-[28px] md:text-[36px] leading-tight tracking-tightest text-bg-primary mb-10 text-center" data-id="element-119">
              What To Expect On The Call.
            </h2>
          </FadeUp>

          <FadeUp delay={0.1} data-id="element-120">
            <div className="w-full aspect-video bg-bg-primary rounded-2xl overflow-hidden relative group cursor-pointer shadow-[0_20px_60px_rgba(14,26,53,0.15)] mb-10" data-id="element-121">
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-122">
                <div className="w-20 h-20 rounded-full bg-accent/15 flex items-center justify-center mb-5 group-hover:bg-accent/25 transition-colors" data-id="element-123">
                  <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center pl-1" data-id="element-124">
                    <Play className="w-6 h-6 text-bg-primary fill-bg-primary" data-id="element-125" />
                  </div>
                </div>
                <span className="font-display font-bold text-white text-lg md:text-xl" data-id="element-126">
                  Watch the 90-second briefing
                </span>
                <span className="text-sm text-white/70 mt-2" data-id="element-127">
                  From [Founder Name], RIA Catalyst
                </span>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <DashedDivider data-id="element-128" />

      {/* 3. Text/Email Confirmation */}
      <section className="w-full px-6 md:px-12 py-20 md:py-28" data-id="element-129">
        <div className="max-w-3xl mx-auto" data-id="element-130">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start" data-id="element-131">
            <FadeUp data-id="element-132">
              <span className="block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-bg-primary/60 mb-6" data-id="element-133">
                One Quick Step
              </span>
              <h2 className="font-display font-bold text-[28px] md:text-[36px] leading-tight tracking-tightest text-bg-primary mb-6" data-id="element-134">
                We'll Text You To Confirm.
              </h2>
              <p className="text-bg-primary/70 text-[17px] leading-relaxed mb-5" data-id="element-135">
                We personally confirm every session before it goes on the
                calendar. It's a small step that keeps our sessions reserved for
                founders who are serious about understanding their firm's
                position — and protects your time from the noise.
              </p>
              <p className="text-bg-primary/70 text-[17px] leading-relaxed" data-id="element-136">
                You'll receive a brief text from our team within the hour. A
                one-word reply is all we need.
              </p>
            </FadeUp>

            <FadeUp delay={0.1} data-id="element-137">
              <div className="bg-bg-primary/[0.03] border border-bg-primary/10 rounded-2xl p-6 md:p-7" data-id="element-138">
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-bg-primary/10" data-id="element-139">
                  <MessageCircle className="w-4 h-4 text-bg-primary/60" data-id="element-140" />
                  <span className="text-xs uppercase tracking-widest text-bg-primary/60 font-semibold" data-id="element-141">
                    Messages
                  </span>
                </div>
                <div className="flex flex-col gap-3" data-id="element-142">
                  <div className="max-w-[85%]" data-id="element-143">
                    <div className="bg-bg-primary/[0.06] border border-bg-primary/10 rounded-2xl rounded-tl-sm px-4 py-3 text-[14px] text-bg-primary leading-snug" data-id="element-144">
                      Hi [Name] — just confirming our strategy session [Day] at
                      [Time] PT. Reply YES to lock it in.
                    </div>
                    <div className="text-[11px] text-bg-primary/50 mt-1 ml-1" data-id="element-145">
                      RIA Catalyst
                    </div>
                  </div>
                  <div className="max-w-[85%] self-end" data-id="element-146">
                    <div className="bg-accent text-bg-primary rounded-2xl rounded-tr-sm px-4 py-3 text-[14px] font-semibold" data-id="element-147">
                      Yes
                    </div>
                  </div>
                  <div className="max-w-[85%]" data-id="element-148">
                    <div className="bg-bg-primary/[0.06] border border-bg-primary/10 rounded-2xl rounded-tl-sm px-4 py-3 text-[14px] text-bg-primary leading-snug" data-id="element-149">
                      Perfect. Calendar invite on the way.
                    </div>
                    <div className="text-[11px] text-bg-primary/50 mt-1 ml-1" data-id="element-150">
                      RIA Catalyst
                    </div>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <DashedDivider data-id="element-151" />

      {/* 5. Who This Is For */}
      <section className="w-full px-6 md:px-12 py-20 md:py-28" data-id="element-152">
        <div className="max-w-3xl mx-auto" data-id="element-153">
          <FadeUp data-id="element-154">
            <span className="block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-bg-primary/60 mb-6" data-id="element-155">
              Before You Tell Anyone
            </span>
          </FadeUp>
          <FadeUp delay={0.05} data-id="element-156">
            <h2 className="font-display font-bold text-[28px] md:text-[36px] leading-tight tracking-tightest text-bg-primary mb-12" data-id="element-157">
              You Haven't Told Anyone You're Thinking About This.
            </h2>
          </FadeUp>
          <ul className="flex flex-col gap-5" data-id="element-158">
            {qualifiers.map((q, i) => <FadeUp key={i} delay={0.05 * i} data-id="element-159">
                <li className="flex items-start gap-4 text-bg-primary/80 text-lg leading-relaxed" data-id="element-160">
                  <span className="text-accent text-xl leading-none mt-1.5 shrink-0 font-bold" data-id="element-161">
                    —
                  </span>
                  <span data-id="element-162">{q}</span>
                </li>
              </FadeUp>)}
          </ul>
        </div>
      </section>

      <DashedDivider data-id="element-163" />

      {/* 7. Closing Note */}
      <section className="w-full px-6 md:px-12 py-20 md:py-28" data-id="element-164">
        <div className="max-w-2xl mx-auto" data-id="element-165">
          <FadeUp data-id="element-166">
            <span className="block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-bg-primary/60 mb-6" data-id="element-167">
              A Note From The Founder
            </span>
          </FadeUp>
          <FadeUp delay={0.05} data-id="element-168">
            <div className="text-bg-primary/80 text-lg md:text-xl leading-relaxed space-y-5" data-id="element-169">
              <p data-id="element-170">The call is worth taking whether you ever sell or not.</p>
              <p data-id="element-171">
                I spent years inside some of the largest RIAs in the country —
                at Certuity, Cresset, and IEQ Capital — and the one thing I saw
                consistently was founders making the most important financial
                decision of their lives without the right information in front
                of them.
              </p>
              <p data-id="element-172">
                Most of the founders I talk to walk away from our first
                conversation with two or three things they didn't know about
                their own firm's market position — and a clearer sense of
                whether the next five years should look different.
              </p>
              <p data-id="element-173">
                We're not here to push you toward a deal. We're here to make
                sure that if the moment ever comes, you've already done the
                thinking.
              </p>
              <div className="flex items-center gap-4 pt-4" data-id="element-174">
                <img src="https://cdn.magicpatterns.com/uploads/kATVZXPMHxUDmrz9E3ZzU7/pasted-image.png" alt="Julien Baneux, Founder & CEO of RIA Catalyst" className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover shrink-0" data-id="element-175" />
                <div className="flex flex-col" data-id="element-176">
                  <span className="text-bg-primary font-semibold text-base md:text-lg leading-tight" data-id="element-177">
                    — Julien Baneux
                  </span>
                  <span className="text-bg-primary/60 text-sm md:text-base leading-tight" data-id="element-178">
                    Founder & CEO, RIA Catalyst
                  </span>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full px-6 md:px-12 pb-12 pt-8" data-id="element-179">
        <p className="text-center text-[12px] text-bg-primary/50" data-id="element-180">
          RIA Catalyst © 2026
        </p>
      </footer>
    </div>;
}